import React from 'react'
import { useNavigate } from 'react-router-dom'

const TermsOfServicePage: React.FC = () => {
    const navigate = useNavigate()

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Hero Section */}
            <section className="bg-gray-100 dark:bg-gray-800 py-12 sm:py-16 md:py-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-4 sm:mb-6 text-gray-900 dark:text-white">Terms of Service</h1>
                    <p className="text-sm sm:text-base md:text-lg font-body leading-relaxed max-w-2xl mx-auto text-gray-700 dark:text-gray-300">
                        These Terms of Service govern your use of SideGO's platform and services.
                        Please read them carefully before using our services.
                    </p>
                    <div className="text-xs sm:text-sm mt-3 sm:mt-4">
                        <span className="text-gray-500 dark:text-gray-400">Last updated: November 2025</span>
                    </div>
                </div>
            </section>

            {/* Content Section */}
            <section className="py-12 sm:py-16 md:py-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

                    <div className="prose prose-gray dark:prose-invert max-w-none">
                        <div className="space-y-6 sm:space-y-8">
                            <section>
                                <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-heading font-semibold mb-3 sm:mb-4 text-gray-900 dark:text-white">Acceptance of Terms</h2>
                                <div className="text-sm sm:text-base font-body leading-relaxed space-y-3 sm:space-y-4 text-gray-700 dark:text-gray-300">
                                    <p>
                                        By accessing or using SideGO's services, you agree to be bound by these Terms of Service
                                        and all applicable laws and regulations. If you do not agree with any part of these terms,
                                        you may not use our services.
                                    </p>
                                </div>
                            </section>

                            <section>
                                <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-heading font-semibold mb-3 sm:mb-4 text-gray-900 dark:text-white">Description of Service</h2>
                                <div className="text-sm sm:text-base font-body leading-relaxed space-y-3 sm:space-y-4 text-gray-700 dark:text-gray-300">
                                    <p>
                                        SideGO is an online marketplace that connects individuals and businesses with freelancers
                                        and service providers for various gig work and side hustle opportunities.
                                    </p>
                                    <h3 className="text-base sm:text-lg md:text-xl font-heading font-semibold mt-4 sm:mt-6 mb-2 sm:mb-3 text-gray-900 dark:text-white">Platform Features</h3>
                                    <ul className="space-y-1.5 sm:space-y-2 ml-4 sm:ml-6 list-disc text-sm sm:text-base">
                                        <li>Gig posting and browsing</li>
                                        <li>User profiles and portfolios</li>
                                        <li>Messaging and communication tools</li>
                                        <li>Application and hiring process</li>
                                        <li>Rating and review system</li>
                                        <li>Payment processing (through third-party providers)</li>
                                    </ul>
                                </div>
                            </section>

                            <section>
                                <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-heading font-semibold mb-3 sm:mb-4 text-gray-900 dark:text-white">User Accounts</h2>
                                <div className="text-sm sm:text-base font-body leading-relaxed space-y-3 sm:space-y-4 text-gray-700 dark:text-gray-300">
                                    <h3 className="text-base sm:text-lg md:text-xl font-heading font-semibold mt-4 sm:mt-6 mb-2 sm:mb-3 text-gray-900 dark:text-white">Account Registration</h3>
                                    <ul className="space-y-1.5 sm:space-y-2 ml-4 sm:ml-6 list-disc text-sm sm:text-base">
                                        <li>You must be at least 18 years old to create an account</li>
                                        <li>You must provide accurate and complete information</li>
                                        <li>You are responsible for maintaining the security of your account</li>
                                        <li>You must notify us immediately of any unauthorized use</li>
                                    </ul>

                                    <h3 className="text-base sm:text-lg md:text-xl font-heading font-semibold mt-4 sm:mt-6 mb-2 sm:mb-3 text-gray-900 dark:text-white">Account Responsibilities</h3>
                                    <ul className="space-y-1.5 sm:space-y-2 ml-4 sm:ml-6 list-disc text-sm sm:text-base">
                                        <li>Keep your login credentials secure and confidential</li>
                                        <li>Update your account information when necessary</li>
                                        <li>Comply with all applicable laws and regulations</li>
                                        <li>Respect other users and maintain professionalism</li>
                                    </ul>
                                </div>
                            </section>

                            <section>
                                <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-heading font-semibold mb-3 sm:mb-4 text-gray-900 dark:text-white">User Conduct</h2>
                                <div className="text-sm sm:text-base font-body leading-relaxed space-y-3 sm:space-y-4 text-gray-700 dark:text-gray-300">
                                    <p>You agree not to:</p>
                                    <ul className="space-y-1.5 sm:space-y-2 ml-4 sm:ml-6 list-disc text-sm sm:text-base">
                                        <li>Post false, misleading, or fraudulent content</li>
                                        <li>Engage in harassment, abuse, or discriminatory behavior</li>
                                        <li>Violate any applicable laws or regulations</li>
                                        <li>Infringe on intellectual property rights</li>
                                        <li>Distribute spam, viruses, or malicious content</li>
                                        <li>Attempt to circumvent platform fees or policies</li>
                                        <li>Create multiple accounts to evade restrictions</li>
                                    </ul>
                                </div>
                            </section>

                            <section>
                                <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-heading font-semibold mb-3 sm:mb-4 text-gray-900 dark:text-white">Gig Posting and Applications</h2>
                                <div className="text-sm sm:text-base font-body leading-relaxed space-y-3 sm:space-y-4 text-gray-700 dark:text-gray-300">
                                    <h3 className="text-base sm:text-lg md:text-xl font-heading font-semibold mt-4 sm:mt-6 mb-2 sm:mb-3 text-gray-900 dark:text-white">For Employers</h3>
                                    <ul className="space-y-1.5 sm:space-y-2 ml-4 sm:ml-6 list-disc text-sm sm:text-base">
                                        <li>Provide accurate and detailed gig descriptions</li>
                                        <li>Set fair compensation and clear expectations</li>
                                        <li>Respond to applications in a timely manner</li>
                                        <li>Honor commitments made to hired freelancers</li>
                                    </ul>

                                    <h3 className="text-base sm:text-lg md:text-xl font-heading font-semibold mt-4 sm:mt-6 mb-2 sm:mb-3 text-gray-900 dark:text-white">For Freelancers</h3>
                                    <ul className="space-y-1.5 sm:space-y-2 ml-4 sm:ml-6 list-disc text-sm sm:text-base">
                                        <li>Only apply for gigs you can realistically complete</li>
                                        <li>Provide accurate information about your skills and experience</li>
                                        <li>Deliver work according to agreed specifications and deadlines</li>
                                        <li>Communicate professionally with clients</li>
                                    </ul>
                                </div>
                            </section>

                            <section>
                                <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-heading font-semibold mb-3 sm:mb-4 text-gray-900 dark:text-white">Payment and Fees</h2>
                                <div className="text-sm sm:text-base font-body leading-relaxed space-y-3 sm:space-y-4 text-gray-700 dark:text-gray-300">
                                    <p>
                                        SideGO may charge fees for certain services. All applicable fees will be clearly
                                        displayed before you complete any transaction.
                                    </p>
                                    <ul className="space-y-1.5 sm:space-y-2 ml-4 sm:ml-6 list-disc text-sm sm:text-base">
                                        <li>Payment processing is handled by third-party providers</li>
                                        <li>Platform fees may apply to transactions</li>
                                        <li>Refunds are subject to our refund policy</li>
                                        <li>Users are responsible for applicable taxes</li>
                                    </ul>
                                </div>
                            </section>

                            <section>
                                <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-heading font-semibold mb-3 sm:mb-4 text-gray-900 dark:text-white">Intellectual Property</h2>
                                <div className="text-sm sm:text-base font-body leading-relaxed space-y-3 sm:space-y-4 text-gray-700 dark:text-gray-300">
                                    <p>
                                        SideGO respects intellectual property rights. By using our platform, you agree that:
                                    </p>
                                    <ul className="space-y-1.5 sm:space-y-2 ml-4 sm:ml-6 list-disc text-sm sm:text-base">
                                        <li>You own or have rights to content you post</li>
                                        <li>You grant SideGO limited rights to use your content for platform operations</li>
                                        <li>You will not infringe on others' intellectual property rights</li>
                                        <li>SideGO retains rights to platform technology and branding</li>
                                    </ul>
                                </div>
                            </section>

                            <section>
                                <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-heading font-semibold mb-3 sm:mb-4 text-gray-900 dark:text-white">Limitation of Liability</h2>
                                <div className="text-sm sm:text-base font-body leading-relaxed space-y-3 sm:space-y-4 text-gray-700 dark:text-gray-300">
                                    <p>
                                        To the maximum extent permitted by law, SideGO shall not be liable for any indirect,
                                        incidental, special, consequential, or punitive damages arising from your use of the platform.
                                    </p>
                                </div>
                            </section>

                            <section>
                                <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-heading font-semibold mb-3 sm:mb-4 text-gray-900 dark:text-white">Termination</h2>
                                <div className="text-sm sm:text-base font-body leading-relaxed space-y-3 sm:space-y-4 text-gray-700 dark:text-gray-300">
                                    <p>
                                        We may terminate or suspend your account at any time for violation of these terms or
                                        for any other reason at our sole discretion.
                                    </p>
                                </div>
                            </section>

                            <section>
                                <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-heading font-semibold mb-3 sm:mb-4 text-gray-900 dark:text-white">Changes to Terms</h2>
                                <div className="text-sm sm:text-base font-body leading-relaxed space-y-3 sm:space-y-4 text-gray-700 dark:text-gray-300">
                                    <p>
                                        We reserve the right to modify these terms at any time. Changes will be effective
                                        immediately upon posting. Your continued use of the platform constitutes acceptance
                                        of the modified terms.
                                    </p>
                                </div>
                            </section>

                            <section>
                                <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-heading font-semibold mb-3 sm:mb-4 text-gray-900 dark:text-white">Contact Us</h2>
                                <div className="text-sm sm:text-base font-body leading-relaxed text-gray-700 dark:text-gray-300">
                                    <p className="mb-4 sm:mb-6">
                                        If you have any questions about these Terms of Service, please reach out to us through our contact form.
                                    </p>
                                    <button
                                        onClick={() => navigate('/contact')}
                                        className="px-6 sm:px-8 py-2.5 sm:py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium text-sm sm:text-base rounded-lg transition-colors inline-block"
                                    >
                                        Contact Support
                                    </button>
                                </div>
                            </section>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    )
}

export default TermsOfServicePage